// src/data/resourcePages.ts
// ---------------------------
// Repository for `resource_pages` (Resources → Invoicing / Responders; db-109).
// Internal staff read; Supervisors + SuperUsers write (RLS). Nestable tree via
// `parent_id`. Soft-delete via `deactivated`.

import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

export type ResourcePageRow = Database["public"]["Tables"]["resource_pages"]["Row"];
export type ResourcePageInsert = Database["public"]["Tables"]["resource_pages"]["Insert"];
export type ResourcePageUpdate = Database["public"]["Tables"]["resource_pages"]["Update"];

const COLS = "id, section, parent_id, title, body, sort_order, deactivated";

// All active pages in a section, in sort order. Null on error / no client.
export async function listResourcePages(section: string): Promise<ResourcePageRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("resource_pages")
    .select(COLS)
    .eq("section", section)
    .eq("deactivated", false)
    .order("sort_order", { ascending: true });
  if (error) {
    logger.warn("listResourcePages failed:", error.message);
    return null;
  }
  return (data ?? []) as unknown as ResourcePageRow[];
}

// All active pages across EVERY section (for the Resources search — reads all of
// General / Invoicing / Responders at once). Ordered by section then sort order.
export async function listAllResourcePages(): Promise<ResourcePageRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("resource_pages")
    .select(COLS)
    .eq("deactivated", false)
    .order("section", { ascending: true })
    .order("sort_order", { ascending: true });
  if (error) {
    logger.warn("listAllResourcePages failed:", error.message);
    return null;
  }
  return (data ?? []) as unknown as ResourcePageRow[];
}

export async function createResourcePage(
  row: ResourcePageInsert
): Promise<{ data: ResourcePageRow | null; error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { data: null, error: { message: "No database connection." } };
  const { data, error } = await c.from("resource_pages").insert(row).select(COLS).single();
  if (error) logger.warn("createResourcePage failed:", error.message);
  return { data: (data as unknown as ResourcePageRow) ?? null, error };
}

export async function updateResourcePage(
  id: string,
  patch: ResourcePageUpdate
): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: { message: "No database connection." } };
  const { error } = await c
    .from("resource_pages")
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) logger.warn("updateResourcePage failed:", error.message);
  return { error };
}

// Soft delete (schema convention). Callers cascade to descendants via the tree.
export async function deactivateResourcePages(
  ids: string[]
): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: { message: "No database connection." } };
  if (ids.length === 0) return { error: null };
  const { error } = await c
    .from("resource_pages")
    .update({ deactivated: true, updated_at: new Date().toISOString() })
    .in("id", ids);
  if (error) logger.warn("deactivateResourcePages failed:", error.message);
  return { error };
}
